import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api, extractError } from "../lib/api";
import {
  buildSalesContractPayload,
  brokerLabel,
  buyerLabel,
  deliveryLabel,
  emptySalesContractForm,
  packingLabel,
  paymentLabel,
  productSummary,
  qualityTypeLabel,
  quantityLabel,
  rateLabel,
  sortPartiesByName,
  upsertParty,
  validateSalesContractForm,
  varietyLabel,
} from "../lib/salesContractOptions";
import { PageHeader, SectionCard, Pill } from "../components/Ui";
import { Button } from "../components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { toast } from "sonner";
import { Plus } from "lucide-react";
import SalesContractFormFields from "../components/SalesContractFormFields";
import SalesContractDetailDialog from "../components/SalesContractDetailDialog";
import DataTableBlock, { dataTableListTitle } from "../components/DataTableBlock";
import DataTableExportButton from "../components/DataTableExportButton";
import { useBootstrapList } from "../hooks/useBootstrapList";
import { useBootstrapWS } from "../hooks/useBootstrapWS";
import { LIST_PAGE_SIZE_OPTIONS } from "../lib/listPagination";
import { SALES_CONTRACT_COLUMNS } from "./salesContractColumns";

const SALES_CONTRACTS_WS_TYPES = ["sales_contract"];

export default function SalesContractsPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const list = useBootstrapList({
    path: "/sales-contracts/bootstrap",
    columns: SALES_CONTRACT_COLUMNS,
    defaultSort: { id: "contract_date", desc: true },
    wsTypes: SALES_CONTRACTS_WS_TYPES,
  });
  useBootstrapWS(SALES_CONTRACTS_WS_TYPES, list.reload);
  const partyPickers = useMemo(
    () => ({
      buyers: list.extras.buyers ?? [],
      brokers: list.extras.brokers ?? [],
    }),
    [list.extras.buyers, list.extras.brokers],
  );
  const [pageTab, setPageTab] = useState("register");
  const [detailId, setDetailId] = useState(() => {
    const raw = searchParams.get("contract");
    return raw ? Number(raw) || raw : null;
  });
  const [form, setForm] = useState(emptySalesContractForm);
  const [submitting, setSubmitting] = useState(false);
  const [buyers, setBuyers] = useState([]);
  const [brokers, setBrokers] = useState([]);

  useEffect(() => {
    setBuyers(sortPartiesByName(partyPickers.buyers));
    setBrokers(sortPartiesByName(partyPickers.brokers));
  }, [partyPickers.buyers, partyPickers.brokers]);

  useEffect(() => {
    const raw = searchParams.get("contract");
    if (!raw) return;
    setDetailId(Number(raw) || raw);
    setPageTab("register");
  }, [searchParams]);

  useEffect(() => {
    const current = searchParams.get("contract") || "";
    const nextId = detailId != null ? String(detailId) : "";
    if (current === nextId) return;
    const next = new URLSearchParams(searchParams);
    if (detailId != null) next.set("contract", String(detailId));
    else next.delete("contract");
    setSearchParams(next, { replace: true });
  }, [detailId, searchParams, setSearchParams]);

  const onSubmit = async (e) => {
    e.preventDefault();
    const err = validateSalesContractForm(form);
    if (err) {
      toast.error(err);
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/sales-contracts", buildSalesContractPayload(form));
      toast.success("Sales contract registered");
      setForm(emptySalesContractForm());
      setPageTab("register");
      list.reload();
    } catch (submitErr) {
      toast.error(extractError(submitErr, "Could not save sales contract"));
    } finally {
      setSubmitting(false);
    }
  };

  const registerCount = list.meta.count ?? 0;

  return (
    <div className="space-y-6">
      <PageHeader
        title="Sales Contract"
        subtitle="Rice sales contracts — quality, variety, quantity, rate, payment & delivery terms."
        actions={(
          <Button
            type="button"
            className="bg-bansal-sage hover:bg-bansal-sageDark text-white"
            onClick={() => {
              setForm(emptySalesContractForm());
              setPageTab("new");
            }}
            data-testid="sales-contract-new"
          >
            <Plus className="w-4 h-4 mr-2" />
            New contract
          </Button>
        )}
      />

      <Tabs value={pageTab} onValueChange={setPageTab}>
        <TabsList className="bg-card border border-border p-1 h-auto w-full max-w-full justify-start overflow-x-auto flex-nowrap [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
          <TabsTrigger value="new" className="text-xs sm:text-sm shrink-0 min-h-10">
            New contract
          </TabsTrigger>
          <TabsTrigger value="register" className="text-xs sm:text-sm shrink-0 min-h-10">
            Contract register ({registerCount})
          </TabsTrigger>
        </TabsList>

        <TabsContent value="new" className="mt-4">
          <SectionCard title="Register sales contract">
            <form onSubmit={onSubmit} className="space-y-4">
              <SalesContractFormFields
                form={form}
                setForm={setForm}
                buyers={buyers}
                brokers={brokers}
                onBuyersChange={(party) => setBuyers((prev) => upsertParty(prev, party))}
                onBrokersChange={(party) => setBrokers((prev) => upsertParty(prev, party))}
              />
              <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-2 pt-2">
                <Button type="button" variant="outline" onClick={() => setPageTab("register")}>
                  Cancel
                </Button>
                <Button
                  type="submit"
                  disabled={submitting}
                  className="bg-bansal-sage hover:bg-bansal-sageDark text-white"
                  data-testid="sales-contract-submit"
                >
                  {submitting ? "Saving…" : "Register contract"}
                </Button>
              </div>
            </form>
          </SectionCard>
        </TabsContent>

        <TabsContent value="register" className="mt-4">
          <DataTableBlock
            title={dataTableListTitle("sales contracts", list.meta.count, {
              filtered: list.table.rows.length,
              hasFilters: list.table.hasActiveFilters,
            })}
            columns={SALES_CONTRACT_COLUMNS}
            table={list.table}
            loading={list.loading}
            totalCount={list.meta.count}
            testId="sales-contracts-table"
            minWidth="1200px"
            emptyMessage="No sales contracts yet."
            emptyFilteredMessage="No contracts match your filters."
            pagination={{ ...list.pagination, pageSizeOptions: LIST_PAGE_SIZE_OPTIONS }}
            exportButton={(
              <DataTableExportButton
                apiPath="/sales-contracts"
                params={list.exportParams}
                columns={SALES_CONTRACT_COLUMNS}
                filename="sales-contracts.xlsx"
                testId="sales-contracts-export"
              />
            )}
            renderRow={(c) => (
              <tr key={c.id} className="border-t hover:bg-accent/60" data-testid={`sac-row-${c.contract_number}`}>
                <td className="px-3 py-2.5 font-mono text-xs">
                  <button
                    type="button"
                    className="text-primary hover:text-primary underline underline-offset-2 font-medium"
                    onClick={() => setDetailId(c.id)}
                    data-testid={`sac-open-${c.contract_number}`}
                  >
                    {c.contract_number}
                  </button>
                </td>
                <td className="px-3 py-2.5 text-xs">{c.contract_date}</td>
                <td className="px-3 py-2.5 text-xs">{buyerLabel(c)}</td>
                <td className="px-3 py-2.5 text-xs">{varietyLabel(c)}</td>
                <td className="px-3 py-2.5 text-xs">{qualityTypeLabel(c)}</td>
                <td className="px-3 py-2.5 text-xs">{quantityLabel(c)}</td>
                <td className="px-3 py-2.5 text-xs">{rateLabel(c)}</td>
                <td className="px-3 py-2.5 text-xs">{paymentLabel(c)}</td>
                <td className="px-3 py-2.5 text-xs">{brokerLabel(c)}</td>
                <td className="px-3 py-2.5 text-xs">{packingLabel(c)}</td>
                <td className="px-3 py-2.5 text-xs">{deliveryLabel(c)}</td>
                <td className="px-3 py-2.5 text-xs">
                  <Pill tone={c.status === "ACTIVE" ? "sage" : "muted"}>{c.status}</Pill>
                </td>
              </tr>
            )}
          />
        </TabsContent>
      </Tabs>

      <SalesContractDetailDialog
        contractId={detailId}
        open={Boolean(detailId)}
        onOpenChange={(next) => { if (!next) setDetailId(null); }}
        onSaved={list.reload}
        onDeleted={list.reload}
        partyPickers={{ buyers, brokers }}
      />
    </div>
  );
}
